// src/components/SideNav.jsx
import { useState, useEffect } from "react";
import {
    BookOpenIcon,
    UsersIcon,
    InboxIcon,
    UserCircleIcon,
    MapPinIcon,
} from "@heroicons/react/24/outline";
import { PowerIcon } from "@heroicons/react/24/solid";

export const SIDEBAR_COLLAPSED_W = "4.99rem";
export const SIDEBAR_EXPANDED_W = "15.99rem";

export default function SideNav({
    selectedStore,
    onNavigate = () => { },
    onLogout = () => { },
    onToggle = () => { },
}) {
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        onToggle(expanded);
    }, [expanded, onToggle]);

    const menu = [
        { key: "books", label: "Manage Books", icon: <BookOpenIcon className="w-5 h-5" /> },
        { key: "members", label: "Manage Members", icon: <UsersIcon className="w-5 h-5" /> },
        { key: "orders", label: "Order Fulfillment", icon: <InboxIcon className="w-5 h-5" /> },
    ];

    const width = expanded ? SIDEBAR_EXPANDED_W : SIDEBAR_COLLAPSED_W;

    return (
        <aside
            className="fixed left-0 top-0 h-screen bg-white border-r border-gray-200 z-40"
            style={{
                width,
                transition: "width 180ms ease-out",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden", // 🚫 no scrollbar
            }}
        >
            {/* ---------------- LOGO ---------------- */}
            <div
                onClick={() => setExpanded((s) => !s)}
                className="relative flex items-center h-16 border-b cursor-pointer overflow-hidden shrink-0"
            >
                {/* Expanded Logo */}
                <img
                    src="/assets/squiggles-logo.png"
                    alt="Squiggles"
                    className={`absolute left-6 h-9 w-auto object-contain transition-opacity duration-180 ease-out ${expanded ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                        }`}
                />

                {/* Collapsed Logo */}
                <img
                    src="/assets/logo.svg"
                    alt="Squiggles Icon"
                    className={`absolute left-1/2 -translate-x-1/2 h-9 w-auto object-contain transition-opacity duration-180 ease-out ${expanded ? "opacity-0 pointer-events-none" : "opacity-100 pointer-events-auto"
                        }`}
                />
            </div>

            {/* ---------------- MENU (TOP) ---------------- */}
            <nav className="flex-1 pt-4">
                {menu.map((m) => (
                    <button
                        key={m.key}
                        onClick={() => onNavigate(m.key)}
                        className="w-full flex items-center h-12 hover:bg-gray-50 text-left cursor-pointer overflow-hidden"
                    >
                        {/* Icon Container */}
                        <div className="w-[4.99rem] flex-shrink-0 flex justify-center items-center">
                            <span className="text-gray-700">{m.icon}</span>
                        </div>

                        {/* Label */}
                        <span
                            className={`text-gray-800 whitespace-nowrap text-sm font-medium transition-opacity transition-transform transition-[max-width] duration-180 ease-out ${expanded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 pointer-events-none"
                                }`}
                            style={{
                                maxWidth: expanded ? "10rem" : "0px",
                            }}
                        >
                            {m.label}
                        </span>
                    </button>
                ))}
            </nav>

            {/* ---------------- BOTTOM FIXED SECTION ---------------- */}
            <div className="border-t border-gray-200">
                {/* Store Info */}
                <div className="h-16 flex items-center min-w-0 overflow-hidden">
                    {/* Icon Container */}
                    <div className="w-[4.99rem] flex-shrink-0 flex justify-center items-center">
                        <MapPinIcon className="w-5 h-5 text-gray-600" />
                    </div>

                    {/* Store Info Details */}
                    <div
                        className={`transition-opacity transition-transform transition-[max-width] duration-180 ease-out flex flex-col min-w-0 ${expanded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 pointer-events-none"
                            }`}
                        style={{
                            maxWidth: expanded ? "10rem" : "0px",
                        }}
                    >
                        {selectedStore ? (
                            <>
                                <div className="font-semibold text-gray-900 text-sm truncate">
                                    {selectedStore.name}
                                </div>
                                <div className="text-gray-500 text-[10px] leading-tight truncate">
                                    {selectedStore.location}, {selectedStore.id}, {selectedStore.status}
                                </div>
                            </>
                        ) : (
                            <div className="text-blue-600 text-sm truncate">No store selected</div>
                        )}
                    </div>
                </div>

                {/* Profile */}
                <button
                    onClick={() => onNavigate("profile")}
                    className="w-full flex items-center h-12 hover:bg-gray-50 text-left cursor-pointer overflow-hidden"
                >
                    {/* Icon Container */}
                    <div className="w-[4.99rem] flex-shrink-0 flex justify-center items-center">
                        <UserCircleIcon className="w-5 h-5 text-gray-700" />
                    </div>
                    <span
                        className={`text-gray-800 whitespace-nowrap text-sm font-medium transition-opacity transition-transform transition-[max-width] duration-180 ease-out ${expanded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 pointer-events-none"
                            }`}
                        style={{
                            maxWidth: expanded ? "10rem" : "0px",
                        }}
                    >
                        My Profile
                    </span>
                </button>

                {/* Logout */}
                <button
                    onClick={onLogout}
                    className="w-full flex items-center h-12 hover:bg-gray-50 text-left cursor-pointer overflow-hidden"
                >
                    {/* Icon Container */}
                    <div className="w-[4.99rem] flex-shrink-0 flex justify-center items-center">
                        <PowerIcon className="w-5 h-5 text-gray-700" />
                    </div>
                    <span
                        className={`text-gray-800 whitespace-nowrap text-sm font-medium transition-opacity transition-transform transition-[max-width] duration-180 ease-out ${expanded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 pointer-events-none"
                            }`}
                        style={{
                            maxWidth: expanded ? "10rem" : "0px",
                        }}
                    >
                        Logout
                    </span>
                </button>
            </div>
        </aside>
    );
}
